import { dispatchAction } from './action$';
import { Action } from './types/Action';
import { ActionDispatcher } from './types/helpers';

export const unsupportedFunctionSymbol = Symbol('Unsupported function');

/**
 * Indicates that someone tried to use `toDispatchingProps`/`PropsReturningActions` on
 * a prop type that had functions which are not supported.
 *
 * Only void functions (performing side effects) are supported by these utils. Functions which
 * return values are not supported.
 */
type UnsupportedFunction = {
  // We put this in an object type to ensure IDEs will display the type name
  // instead of just inlining the type
  [unsupportedFunctionSymbol]: never;
};

type UnknownFunction = (...props: readonly any[]) => unknown;
type AnyVoidFunction = (...args: readonly any[]) => void;
type AnyActionReturningFunction = (...args: readonly any[]) => Action<unknown>;
type FunctionReturningAction<F extends UnknownFunction> =
  AnyVoidFunction extends F
    ? (...args: Parameters<F>) => Action<unknown>
    : UnsupportedFunction;
type FunctionDispatchingAction<F extends UnknownFunction> =
  F extends AnyActionReturningFunction
    ? (...args: Parameters<F>) => void
    : UnsupportedFunction;
type PropReturningAction<P> = P extends UnknownFunction
  ? FunctionReturningAction<P>
  : P extends Array<unknown>
    ? PropsReturningActions<P>
    : P extends Record<any, unknown>
      ? PropsReturningActions<P>
      : P;
type PropDispatchingAction<P> = P extends UnknownFunction
  ? FunctionDispatchingAction<P>
  : P extends Array<unknown>
    ? PropsDispatchingActions<P>
    : P extends Record<any, unknown>
      ? PropsDispatchingActions<P>
      : P;

/**
 * Converts the prop type for a component into a type suitable for `toDispatchingProps`.
 */
export type PropsReturningActions<T> = T extends
  | Record<any, unknown>
  | Array<unknown>
  ? {
      [key in keyof T]: PropReturningAction<T[key]>;
    }
  : T;

type PropsDispatchingActions<T> = T extends
  | Record<any, unknown>
  | Array<unknown>
  ? {
      [key in keyof T]: PropDispatchingAction<T[key]>;
    }
  : T;

/**
 * Testable variant of `toDispatchingProps` that takes a dispatcher as an argument.
 * The main `toDispatchingProps` cannot take a second argument, because it conflicts
 * with rxjs `map` operator.
 */
export const toDispatchingPropsInternal = <T>(
  prop: T,
  dispatch: ActionDispatcher
): PropsDispatchingActions<T> => {
  if (isFunction(prop)) {
    return ((...args: unknown[]) => {
      const action = prop(...args) as Action<unknown>;

      if (!action) {
        // eslint-disable-next-line no-console
        console.warn(
          'You have passed a function to toDispatchingProps, which does not return an action.',
          prop.name || prop.toString()
        );
        return;
      }

      return dispatch(action);
    }) as PropsDispatchingActions<T>;
  }

  if (isArray(prop)) {
    return prop.map(p =>
      toDispatchingPropsInternal(p, dispatch)
    ) as PropsDispatchingActions<T>;
  }

  if (isObject(prop)) {
    return mapObject(prop, p =>
      toDispatchingPropsInternal(p, dispatch)
    ) as PropsDispatchingActions<T>;
  }

  return prop as PropsDispatchingActions<T>;
};

/**
 * Walk through the props and wrap each function so it will dispatch the returned action.
 *
 * This is useful for having pure prop mappers. Instead of returning prop objects with functions
 * that directly call `dispatchAction`, the functions can just return the action. These are much
 * easier to test and reason about.
 */
export const toDispatchingProps = <T>(prop: T): PropsDispatchingActions<T> =>
  toDispatchingPropsInternal(prop, dispatchAction);

const isArray = (value: unknown): value is Array<unknown> =>
  Array.isArray(value);

const isFunction = (value: unknown): value is UnknownFunction =>
  typeof value === 'function';

const isObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const mapObject = <K extends string | number | symbol, T, R>(
  obj: Record<K, T>,
  fn: (value: T) => R
): Record<K, R> => {
  const result = {} as Record<K, R>;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = fn(obj[key]);
    }
  }

  return result;
};
